/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect } from 'react';
import { FirebaseRemoteConfig } from '@capacitor-firebase/remote-config';
import { ErrorCode, RemoteConfigState, ServiceUnavailableResponse } from './types';
import RemoteConfigSlice, {
  remoteConfigInitialState,
  selectRemoteConfig,
} from './remoteConfig.slice';
import { AppDispatch, useAppDispatch } from '../../../store/rootStore';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { MaintenancePage } from '../../../pages/Maintenance';
import packageJson from '../../../../package.json';
import { VersionMismatch } from '../../../pages/VersionMismatch';
import { compareVersions } from '../utils/compareVersions';
import withFirebaseSafety from '../utils/withFirebaseSafety';
import apiClient from '../../api-client';
import { AxiosError } from 'axios';

const logValue = (message?: unknown, ...optionalParams: unknown[]) => {
  if (process.env.NODE_ENV !== 'production') {
    console.debug(message, optionalParams);
  }
};

export const activate = async () => {
  await FirebaseRemoteConfig.activate().catch(console.log);
};

export const fetchAndActivate = async () => {
  await FirebaseRemoteConfig.fetchAndActivate().catch(console.log);
};

export const fetchConfig = async (interval: number) => {
  await FirebaseRemoteConfig.fetchConfig({
    minimumFetchIntervalInSeconds: interval,
  }).catch(console.log);
};

export const updateKeyCallback = async (
  key: string,
  dispatch: AppDispatch,
  config: RemoteConfigState,
) => {
  // Assuming you want to switch based on the type of value for the key 'exampleKey'
  const valueType = typeof remoteConfigInitialState[key];
  // Switch statement based on the type of value
  switch (valueType) {
    case 'string':
      // Handle string type
      {
        const { value } = await FirebaseRemoteConfig.getString({
          key: key,
        });
        if (value !== config[key]) {
          logValue('Getting key was: ', key, value);
          dispatch(RemoteConfigSlice.actions.updateKey({ key, value }));
        }
      }

      break;
    case 'number':
      // Handle number type
      {
        const { value } = await FirebaseRemoteConfig.getNumber({
          key: key,
        });
        if (value !== config[key]) {
          logValue('Getting key was: ', key, value);
          dispatch(RemoteConfigSlice.actions.updateKey({ key, value }));
        }
      }
      break;
    case 'boolean':
      // Handle boolean type
      {
        const { value } = await FirebaseRemoteConfig.getBoolean({
          key: key,
        });
        if (value !== config[key]) {
          logValue('Getting key was: ', key, value);
          dispatch(RemoteConfigSlice.actions.updateKey({ key, value }));
        }
      }
      break;
    case 'object':
      {
        const { value } = await FirebaseRemoteConfig.getString({
          key: key,
        });
        if (value !== config[key]) {
          const obj = JSON.parse(value);
          logValue('Getting key was: ', key, value);
          dispatch(RemoteConfigSlice.actions.updateKey({ key, value: obj }));
        }
      }
      break;
    default:
      console.log('Getting key was empty');
      console.log(valueType);
      break;
  }
};
export const addConfigUpdateListener = async (dispatch: AppDispatch): Promise<string> => {
  const config = useSelector(selectRemoteConfig);
  const callbackId = await FirebaseRemoteConfig.addConfigUpdateListener(async (event, error) => {
    if (error) {
      console.error(error);
    } else {
      if (event?.updatedKeys.length) {
        await FirebaseRemoteConfig.fetchAndActivate();
        event.updatedKeys.forEach((key) => updateKeyCallback(key, dispatch, config));
      }
    }
  });
  return callbackId;
};

export const removeConfigUpdateListener = async (callbackId: string): Promise<void> => {
  await FirebaseRemoteConfig.removeConfigUpdateListener({
    id: callbackId,
  });
};

export const removeAllListeners = async (): Promise<void> => {
  await FirebaseRemoteConfig.removeAllListeners();
};

const RemoteConfigComponent: React.FC<{ children: React.ReactNode | React.ReactNode[] }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const [, setIntervals] = React.useState<NodeJS.Timeout>();

  const config = useSelector(selectRemoteConfig);

  // Ensures an update if the BE recieves maintenance mode before the FE
  const maintenanceErrorInterceptor = async (
    error: AxiosError<ServiceUnavailableResponse, unknown>,
  ) => {
    if (
      error.code === 'ERR_BAD_RESPONSE' &&
      error.response?.status === 503 &&
      error.response?.data.type === ErrorCode.ServiceUnavailable
    ) {
      // Clear clache
      await FirebaseRemoteConfig.setMinimumFetchInterval({
        minimumFetchIntervalInSeconds: 0,
      });
      // Fetch and apply
      await fetchAndActivate();
      // Ensure Still maintenance mode
      dispatch(
        RemoteConfigSlice.actions.updateKey({
          key: 'maintenance',
          value: true,
        }),
      );
      // Reset cache interval
      await FirebaseRemoteConfig.setMinimumFetchInterval({
        minimumFetchIntervalInSeconds: config.interval,
      });
    }

    throw error;
  };

  // Effect to update RemoteConfigState when it changes
  useEffect(() => {
    apiClient.axiosInstance.interceptors.response.use(null, maintenanceErrorInterceptor);

    const intervals: number[] = [];
    const listeners: string[] = [];
    if (Capacitor.isNativePlatform()) {
      (async () => {
        // fetchConfig()
        await fetchAndActivate();
        Object.keys(remoteConfigInitialState).forEach((key) => {
          updateKeyCallback(key, dispatch, config);
        });
        const listener = await addConfigUpdateListener(dispatch);
        listeners.push(listener);
        return () => {
          removeConfigUpdateListener(listener);
        };
      })();
    } else {
      (async () => {
        let intervalInSeconds = config.interval;
        let intervalInMilliseconds = config.interval * 1000;

        // If website is in maintenance mode, we can shorten and check more frequently for an update
        if (config.maintenance) {
          intervalInSeconds = 60;
          intervalInMilliseconds = intervalInSeconds * 1000;
        }

        await FirebaseRemoteConfig.setMinimumFetchInterval({
          minimumFetchIntervalInSeconds: intervalInSeconds,
        });

        await fetchAndActivate();
        Object.keys(remoteConfigInitialState).forEach((key) => {
          updateKeyCallback(key, dispatch, config);
        });
        const webInterval = setInterval(() => {
          fetchAndActivate();
          Object.keys(remoteConfigInitialState).forEach((key) => {
            updateKeyCallback(key, dispatch, config);
          });
        }, intervalInMilliseconds);

        setIntervals((prevState) => {
          clearInterval(prevState);
          return webInterval;
        });
      })();
      return () => {
        intervals.forEach((interval) => {
          clearInterval(interval);
        });
        listeners.forEach((listener) => removeConfigUpdateListener(listener));
      };
    }
  }, []);

  return <RemoteConfigStateMachine>{children}</RemoteConfigStateMachine>;
};
// RemoteConfigProvider component
export const RemoteConfigStateMachine: React.FC<{
  children: React.ReactNode | React.ReactNode[];
}> = ({ children }) => {
  const config = useSelector(selectRemoteConfig);
  const isVersionMismatch = (config: RemoteConfigState) => {
    if (config.versionIsIgnored) {
      return false;
    }

    return !compareVersions(config.version, packageJson.version);
  };

  const render = () => {
    if (config.maintenance) {
      return <MaintenancePage />;
    }
    if (isVersionMismatch(config)) {
      return <VersionMismatch expected={config.version} current={packageJson.version} />;
    }

    return children;
  };

  return render();
};

export const RemoteConfigProvider = withFirebaseSafety(RemoteConfigComponent);
